/**
 * Initializes Embla carousels used by the product-list section (layout_type: embla).
 * Uses the same DOM structure and class names as product-recommendations-embla
 * so shared styles apply. Only initializes carousels that are not inside
 * the product-recommendations custom element.
 */

const emblaInstances = new Map();
const resizeHandlers = new Map();

function getSlidesPerPage() {
  const width = window.innerWidth;
  if (width >= 990) return 4;
  if (width >= 750) return 3;
  return 1.4;
}

function initCarousel(wrapper) {
  if (typeof EmblaCarousel === 'undefined') {
    return;
  }

  const carousel = wrapper.querySelector('.nf-product-recommendations-carousel');
  if (!(carousel instanceof HTMLElement)) return;

  const carouselId = carousel.dataset.carouselId || carousel.id;
  if (!carouselId || emblaInstances.has(carouselId)) return;

  const container = carousel.querySelector('.nf-product-recommendations-carousel-container');
  if (!container) return;

  const embla = EmblaCarousel(carousel, {
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    slidesToScroll: 1,
  });

  emblaInstances.set(carouselId, embla);

  const prevButton = wrapper.querySelector(
    `.nf-product-recommendations__button--prev[data-carousel-id="${carouselId}"]`
  );
  const nextButton = wrapper.querySelector(
    `.nf-product-recommendations__button--next[data-carousel-id="${carouselId}"]`
  );

  if (prevButton instanceof HTMLButtonElement) {
    prevButton.addEventListener('click', () => embla.scrollPrev());
  }
  if (nextButton instanceof HTMLButtonElement) {
    nextButton.addEventListener('click', () => embla.scrollNext());
  }

  const dotsContainer = wrapper.querySelector(
    `.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`
  );

  const generateDots = () => {
    if (!dotsContainer) return;

    const slides = container.querySelectorAll('.nf-product-recommendations__slide');
    const totalSlides = slides.length;
    const slidesPerPage = getSlidesPerPage();
    const totalPages = Math.ceil(totalSlides / slidesPerPage);

    if (totalPages <= 1) {
      dotsContainer.innerHTML = '';
      return;
    }

    dotsContainer.innerHTML = '';
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'nf-product-recommendations__dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.setAttribute('data-page-index', i.toString());
      if (i === 0) {
        dot.classList.add('nf-product-recommendations__dot--active');
      }

      const slideIndex = Math.floor(i * slidesPerPage);
      dot.addEventListener('click', () => {
        embla.scrollTo(slideIndex);
      });

      dotsContainer.appendChild(dot);
    }
  };

  generateDots();

  const updateButtonStates = () => {
    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled = !embla.canScrollPrev();
    }
    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = !embla.canScrollNext();
    }

    if (dotsContainer) {
      const slides = container.querySelectorAll('.nf-product-recommendations__slide');
      const totalSlides = slides.length;
      const slidesPerPage = getSlidesPerPage();
      const totalPages = Math.ceil(totalSlides / slidesPerPage);

      if (totalPages <= 1) return;

      const selectedSlideIndex = embla.selectedScrollSnap();
      const currentPage = Math.floor(selectedSlideIndex / slidesPerPage);

      const dots = dotsContainer.querySelectorAll('.nf-product-recommendations__dot');
      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i];
        if (dot instanceof HTMLButtonElement) {
          if (i === currentPage) {
            dot.classList.add('nf-product-recommendations__dot--active');
          } else {
            dot.classList.remove('nf-product-recommendations__dot--active');
          }
        }
      }
    }
  };

  let resizeTimeout = null;
  const handleResize = () => {
    if (resizeTimeout !== null) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(() => {
      generateDots();
      updateButtonStates();
      resizeTimeout = null;
    }, 150);
  };

  window.addEventListener('resize', handleResize);
  resizeHandlers.set(carouselId, handleResize);

  embla.on('select', updateButtonStates);
  embla.on('reInit', updateButtonStates);
  updateButtonStates();

  let visibilityDebounce = null;
  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (visibilityDebounce !== null) {
          clearTimeout(visibilityDebounce);
        }
        visibilityDebounce = setTimeout(() => {
          visibilityDebounce = null;
          embla.reInit();
          generateDots();
          updateButtonStates();
        }, 50);
      }
    },
    { threshold: 0.01, rootMargin: '0px' }
  );
  visibilityObserver.observe(carousel);
}

function initAll() {
  const wrappers = document.querySelectorAll('.nf-product-recommendations-carousel-wrapper');
  for (const wrapper of wrappers) {
    if (wrapper.closest('product-recommendations')) continue;
    initCarousel(wrapper);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-init when Shopify section is re-rendered (theme editor, section API)
document.addEventListener('shopify:section:load', () => {
  initAll();
});
